import { useEffect, useMemo, useState } from 'react';
import { Search, RotateCcw, Receipt } from 'lucide-react';
import { Button, Card, CardBody, Input, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { Toolbar } from '@vnetwork/ui/layout';
import { useStatementServices, type ServiceStatementRow } from '@/api/services';
import { useCreateStatementInvoice, useRollbackStatementInvoice } from '@/api/services-mutations';
import {
  serviceIdCell,
  serviceCustomerCell,
  serviceNameCell,
  textCell,
  dateCell,
  dateRangeCell,
  serviceStatusCell,
} from '../list-configs/service-cell-renderers';
import { paginationView } from '@/pages/customers/pagination-summary';

const PAGE_SIZE = 20;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/** Inline per-row Run/Rollback action cell. The parent passes the callbacks. */
function StatementActions({
  row,
  onRun,
  onRollback,
}: {
  row: ServiceStatementRow;
  onRun: (r: ServiceStatementRow) => void;
  onRollback: (r: ServiceStatementRow) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 'var(--vn-space-1)' }}>
      <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); onRun(row); }}>
        <Receipt size={13} /> Run
      </Button>
      {row.has_active_snapshot && (
        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onRollback(row); }}>
          <RotateCcw size={13} /> Rollback
        </Button>
      )}
    </div>
  );
}

/**
 * Statement list tab — the reconciliation (doi soat) feed from `GET /services/statement`.
 * Each row has inline Run statement (POST /services/:id/invoices/statement) and,
 * when `has_active_snapshot=true`, Rollback (POST /services/:id/invoices/statement/rollback).
 * USER triggers each action; the component builds and presents the confirm dialogs.
 */
export function StatementListSection({
  onRowClick,
}: {
  onRowClick?: (row: ServiceStatementRow) => void;
}) {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);

  // Per-row mutation state — keyed by service_id so concurrent rows stay independent
  const [runTarget, setRunTarget] = useState<ServiceStatementRow | null>(null);
  const [rollbackTarget, setRollbackTarget] = useState<ServiceStatementRow | null>(null);

  const { data, isLoading, isFetching, isError, refetch } = useStatementServices({
    page,
    page_size: PAGE_SIZE,
    q: query || undefined,
  });

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;
  const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  // Run statement mutation — bound to the currently targeted service
  const runMutation = useCreateStatementInvoice(runTarget?.service_id ?? 0);
  const rollbackMutation = useRollbackStatementInvoice(rollbackTarget?.service_id ?? 0);

  const handleRunConfirm = () => {
    if (!runTarget) return;
    runMutation.mutate('', {
      onSuccess: () => {
        toast.success('Statement invoice generated');
        setRunTarget(null);
      },
      onError: (err) =>
        toast.error('Failed to run statement', {
          description: err instanceof Error ? err.message : undefined,
        }),
    });
  };

  const handleRollbackConfirm = () => {
    if (!rollbackTarget) return;
    rollbackMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success('Statement rolled back');
        setRollbackTarget(null);
      },
      onError: (err) =>
        toast.error('Failed to rollback statement', {
          description: err instanceof Error ? err.message : undefined,
        }),
    });
  };

  const columns = useMemo((): ColumnDef<ServiceStatementRow, unknown>[] => [
    { id: 'service_id', header: 'ID', cell: ({ row }) => serviceIdCell(row.original) },
    { id: 'product_group_name', header: 'Product group', cell: ({ row }) => textCell(row.original.product_group_name) },
    { id: 'service_name', header: 'Service', cell: ({ row }) => serviceNameCell(row.original.service_name) },
    { id: 'customer', header: 'Customer', cell: ({ row }) => serviceCustomerCell(row.original) },
    { id: 'service_status', header: 'Status', cell: ({ row }) => serviceStatusCell(row.original.service_status) },
    {
      id: 'statement_period',
      header: 'Statement period',
      cell: ({ row }) => dateRangeCell(row.original.statement_startdate, row.original.statement_enddate),
    },
    {
      id: 'rollback_period',
      header: 'Rollback to',
      cell: ({ row }) => dateRangeCell(
        row.original.rollback_to_statement_startdate,
        row.original.rollback_to_statement_enddate,
      ),
    },
    { id: 'service_start_date', header: 'Start', cell: ({ row }) => dateCell(row.original.service_start_date) },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <StatementActions
          row={row.original}
          onRun={setRunTarget}
          onRollback={setRollbackTarget}
        />
      ),
    },
  ], []);

  return (
    <>
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Toolbar
          style={{ marginBottom: 0 }}
          start={
            <Input
              value={rawQuery}
              onChange={(e) => { setRawQuery(e.target.value); setPage(1); }}
              placeholder="Search statement services…"
              startSlot={<Search size={15} />}
              style={{ maxWidth: 320 }}
            />
          }
        />

        {isError ? (
          <Alert
            tone="danger"
            title="Failed to load statement services"
            actions={<Button size="sm" variant="ghost" onClick={() => refetch()}>Retry</Button>}
          />
        ) : isLoading ? (
          <Card flat>
            <CardBody>
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} style={{ height: 24 }} />
                ))}
              </div>
            </CardBody>
          </Card>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(r) => String(r.service_id)}
            onRowClick={onRowClick}
            emptyState={
              <EmptyState
                icon={<Receipt size={24} />}
                title="No statement services found"
                description="Services eligible for reconciliation will appear here."
              />
            }
            footer={
              <Pagination
                page={page}
                pageCount={view.pageCount}
                onPageChange={setPage}
                summary={`${view.summary}${isFetching ? ' …' : ''}`}
              />
            }
          />
        )}
      </div>

      {/* Run statement confirm */}
      <ConfirmDialog
        open={!!runTarget}
        onOpenChange={(open) => { if (!open) setRunTarget(null); }}
        title="Run statement invoice"
        description={`Generate a statement (doi soat) invoice for ${runTarget?.service_name ?? ''}?`}
        confirmLabel="Run"
        loading={runMutation.isPending}
        onConfirm={handleRunConfirm}
      />

      {/* Rollback confirm */}
      <ConfirmDialog
        open={!!rollbackTarget}
        onOpenChange={(open) => { if (!open) setRollbackTarget(null); }}
        title="Rollback statement"
        description={`Roll back the latest statement for ${rollbackTarget?.service_name ?? ''}? This will revert invoices to the previous period.`}
        confirmLabel="Rollback"
        destructive
        loading={rollbackMutation.isPending}
        onConfirm={handleRollbackConfirm}
      />
    </>
  );
}
